import React, { useLayoutEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { InputField } from "@components/Input/InputField";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, ButtonGroup, Form, Stack } from "react-bootstrap";
import { APP_TITLE_SUFFIX, EMAIL_VALIDATE } from "@constants";
import styles from "./styles/ForgotPassword.module.css";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import { forgotPasswordAction, resetPasswordAction } from "@redux/actions";

const requestSchema = Yup.object().shape({
  email: Yup.string()
    .required("Email Address is required")
    .matches(EMAIL_VALIDATE, "Invalid email format"),
});

const resetSchema = Yup.object().shape({
  codeNumber: Yup.string()
    .required("Verification Code is required")
    .matches(/^[0-9]{4}$/, "Invalid Verification Code format"),
  newPassword: Yup.string()
    .required("New Password is required")
    .min(8, "Password must contains at least 8 characters")
    .matches(
      /^(?=.*[a-z])/,
      "Password must contains at least 1 lowercase character"
    )
    .matches(
      /^(?=.*[A-Z])/,
      "Password must contains at least 1 uppercase character"
    )
    .matches(
      /^(?=.*[0-9])/,
      "Password must contains at least 1 digit character"
    )
    .matches(
      /^(?=.*[!@#$%^&*])/,
      "Password must contains at least 1 special character (!@#$%^&*)"
    )
    .max(255, "Password require maximum of 255 characters"),
  comfirmPassword: Yup.string()
    .required("You need to confirm your password")
    .oneOf([Yup.ref("newPassword")], "Your passwords do not match."),
});

const initialRequest = { email: "" };

const initialReset = {
  codeNumber: "",
  newPassword: "",
  comfirmPassword: "",
};

const TABS = { REQUEST: "request", RESET: "reset" };

const ForgotPassword = () => {
  // state
  const [activeTab, setActiveTab] = useState(TABS.REQUEST);

  // variables
  const requestFields = [
    {
      name: "email",
      type: "email",
      placeholder: "Email Address",
      prefix: <i className="bi bi-envelope" />,
    },
  ];

  const resetFields = [
    {
      name: "newPassword",
      type: "password",
      placeholder: "Enter new password",
    },
    {
      name: "comfirmPassword",
      type: "password",
      placeholder: "New password confirmation",
    },
    {
      name: "codeNumber",
      type: "text",
      placeholder: "Enter verification code",
    },
  ];

  // logics before hooks

  // hooks
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const requestFormik = useFormik({
    initialValues: state || initialRequest,
    validationSchema: requestSchema,
    onSubmit: (values) => {
      dispatch(
        forgotPasswordAction({
          data: [values.email],
          onSuccess: () => setActiveTab(TABS.RESET),
        })
      );
    },
  });

  const resetFormik = useFormik({
    initialValues: initialReset,
    validationSchema: resetSchema,
    onSubmit: (values) => {
      dispatch(
        resetPasswordAction({
          data: [
            requestFormik.values.email,
            values.codeNumber,
            values.newPassword,
            values.comfirmPassword,
          ],
          onSuccess: () =>
            navigate("/sign-in", {
              state: {
                email: requestFormik.values.email,
              },
            }),
          onFailure: () =>
            resetFormik.setFieldError(
              "codeNumber",
              "Verification Code is not correct or expired"
            ),
        })
      );
    },
  });

  // logics
  const handleBack = () => setActiveTab(TABS.REQUEST);

  const handleResendCode = () => {
    dispatch(
      forgotPasswordAction({
        data: [requestFormik.values.email],
      })
    );
  };

  // UI partials
  const requestPasswordForm = (
    <Form noValidate onSubmit={requestFormik.handleSubmit}>
      <Stack gap={4}>
        {requestFields.map((field, index) => (
          <Form.Group key={index} controlId={field.name}>
            <InputField
              name={field.name}
              type={field.type}
              placeholder={field.placeholder}
              value={requestFormik.values[field.name]}
              onChange={requestFormik.handleChange}
              onBlur={requestFormik.handleBlur}
              touchedValue={requestFormik.touched[field.name]}
              errorsValue={requestFormik.errors[field.name]}
              prefix={field.prefix || ""}
            />
          </Form.Group>
        ))}
        <Form.Group controlId="submit">
          <Button type="submit" className="w-100">
            Next Step
            <i className="bi bi-arrow-right ml-2" />
          </Button>
        </Form.Group>
      </Stack>
    </Form>
  );

  const restPasswordForm = (
    <Form noValidate onSubmit={resetFormik.handleSubmit}>
      <Stack gap={4}>
        {resetFields.map((field, index) => (
          <Form.Group key={index} controlId={field.name}>
            <InputField
              name={field.name}
              type={field.type}
              placeholder={field.placeholder}
              value={resetFormik.values[field.name]}
              onChange={resetFormik.handleChange}
              onBlur={resetFormik.handleBlur}
              touchedValue={resetFormik.touched[field.name]}
              errorsValue={resetFormik.errors[field.name]}
            />
          </Form.Group>
        ))}
        <div className="text-left">
          Not received the code?{" "}
          <a className={styles.link} onClick={handleResendCode}>
            Resend code
          </a>
        </div>
        <ButtonGroup>
          <Button variant="light" type="button" onClick={handleBack}>
            <i className="bi bi-arrow-left mr-2" />
            Previous Step
          </Button>
          <Button type="submit">Reset Password</Button>
        </ButtonGroup>
      </Stack>
    </Form>
  );

  // effects

  useLayoutEffect(() => {
    document.title = `Forgot Password | ${APP_TITLE_SUFFIX}`;
  }, []);

  // render
  return (
    <Stack gap={4} className={styles.container}>
      <Stack>
        <h2>
          <b>
            {activeTab === TABS.REQUEST
              ? "Request a Password Reset"
              : "Reset your password"}
          </b>
        </h2>
        <span>
          {activeTab === TABS.REQUEST
            ? "We will send a verification code to your email."
            : "Please enter your verification code and new password."}
        </span>
      </Stack>
      <div className={styles.contentWrapper}>
        <div
          className={clsx(
            styles.slideWrapper,
            activeTab === TABS.REQUEST
              ? styles.requestActive
              : styles.resetActive
          )}
        >
          <div className={styles.slideItem}>{requestPasswordForm}</div>
          <div className={styles.slideItem}>{restPasswordForm}</div>
        </div>
      </div>
      <div>
        Back to <Link to={"/sign-in"}>Sign In</Link>
      </div>
    </Stack>
  );
};

/* required: display name */
ForgotPassword.displayName = "ForgotPassword";

/* required: props types */
ForgotPassword.propTypes = {};

/* required: default props */
ForgotPassword.defaultProps = {};

export { ForgotPassword };
